import type {
  AdminDoerGroupKind,
  AdminEscalation,
  AdminEscalationDetail,
  AdminOnboardingView,
  OnboardingStepNumber,
  AdminWeeklyReview,
  AdminWeeklyReviewDetail,
  AdminWeeklyReviewsResponse,
  AgentDeviceListResponse,
  AgentReleaseRolloutPreviewRequest,
  AgentReleaseRolloutPreviewResponse,
  AgentReleaseState,
  BusinessBriefData,
  CategoryWithRecommendedPlaybooks,
  CoachingSessionStatus,
  CreateDoerGroupInput,
  CreateGroupChatInput,
  EscalationQueueFilters,
  EscalationStatus,
  FounderCategoryCreateInput,
  FounderCategoryUpdateInput,
  FounderRiskBand,
  PlaybookReassignment,
  PutAgentReleaseRolloutRequest,
  SensitivityProfile,
  UpdateDoerGroupInput,
  UpdateGroupChatInput,
  UserActivity,
  UserRole,
  WeeklyReviewContent,
  WorkingHours,
} from '@doneverse/shared'
import { queryOptions, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { getNotifier, isConflictError, readErrorBody } from '../api/index.js'
import { contactsOptions } from '../queries/index.js'
import { playbookCmsKeys } from './usePlaybookCms.js'
import { adminService, agentReleaseService, authService } from '../services/index.js'
import type { SchemaField } from '../utils/index.js'

export const adminKeys = {
  stats: ['admin', 'stats'] as const,
  profiles: ['admin', 'profiles'] as const,
  assignableDoers: ['admin', 'assignableDoers'] as const,
  assignableFounders: ['admin', 'assignableFounders'] as const,
  doerGroupOwners: ['admin', 'doerGroupOwners'] as const,
  doerGroupMembers: ['admin', 'doerGroupMembers'] as const,
  activityLogDoers: ['admin', 'activityLogDoers'] as const,
  groupChatMembers: ['admin', 'groupChatMembers'] as const,
  coachSearch: ['admin', 'coachSearch'] as const,
  user: ['admin', 'user'] as const,
  founderBrief: ['admin', 'founderBrief'] as const,
  categories: ['admin', 'categories'] as const,
  categoryDetail: ['admin', 'categoryDetail'] as const,
  doingTeams: ['admin', 'doingTeams'] as const,
  doerDevices: ['admin', 'doerDevices'] as const,
  agentReleases: ['admin', 'agentReleases'] as const,
  doerGroups: ['admin', 'doerGroups'] as const,
  doerGroup: ['admin', 'doerGroup'] as const,
  latestDslTeamSync: ['admin', 'latestDslTeamSync'] as const,
  groupChats: ['admin', 'groupChats'] as const,
  groupChat: ['admin', 'groupChat'] as const,
  playbookBySlug: ['playbookBySlug'] as const,
  playbookDoerSchema: ['playbookDoerSchema'] as const,
  assignableAdmins: ['admin', 'assignableAdmins'] as const,
  userActivity: ['admin', 'userActivity'] as const,
  memberRemovalImpact: ['admin', 'memberRemovalImpact'] as const,
  deactivationImpact: ['admin', 'deactivationImpact'] as const,
  coachingSessions: ['admin', 'coachingSessions'] as const,
  coachingSession: ['admin', 'coachingSession'] as const,
  escalations: ['admin', 'escalations'] as const,
  escalation: ['admin', 'escalation'] as const,
  escalationAssignees: ['admin', 'escalationAssignees'] as const,
  escalationHistory: ['admin', 'escalationHistory'] as const,
  escalationConsole: ['admin', 'escalationConsole'] as const,
  dslAgent: ['admin', 'dslAgent'] as const,
  cxRisk: ['admin', 'cxRisk'] as const,
  weeklyReviews: ['admin', 'weeklyReviews'] as const,
  weeklyReview: ['admin', 'weeklyReview'] as const,
  founderOnboarding: ['admin', 'founderOnboarding'] as const,
}

/**
 * Preview of what removing a doer from a workspace would affect (active
 * playbooks they own + other doers who could take over). Fetched lazily when
 * the removal dialog opens (`enabled`).
 */
export const memberRemovalImpactOptions = (
  workspaceId: string | undefined,
  userId: string | undefined,
  enabled = true,
) =>
  queryOptions({
    queryKey: [...adminKeys.memberRemovalImpact, workspaceId, userId] as const,
    queryFn: () => adminService.getMemberRemovalImpact(workspaceId!, userId!),
    enabled: enabled && !!workspaceId && !!userId,
  })

// Query options
export const adminUserOptions = (userId: string) =>
  queryOptions({
    queryKey: [...adminKeys.user, userId] as const,
    queryFn: () => adminService.fetchUser(userId),
    enabled: !!userId,
  })

export const adminProfilesOptions = (
  page = 1,
  limit = 20,
  search?: string,
  unassigned?: boolean,
  sort?: string,
  order?: 'asc' | 'desc',
  active?: boolean,
  isTest?: boolean,
) =>
  queryOptions({
    queryKey: [
      ...adminKeys.profiles,
      { page, limit, search, unassigned, sort, order, active, isTest },
    ] as const,
    queryFn: () => adminService.fetchProfiles(page, limit, search, unassigned, sort, order, active, isTest),
  })

/**
 * Impact preview for disabling a user — fetched lazily when the disable dialog
 * opens (`enabled`) so the admin sees concrete counts before confirming.
 */
export const deactivationImpactOptions = (userId: string | undefined, enabled = true) =>
  queryOptions({
    queryKey: [...adminKeys.deactivationImpact, userId] as const,
    queryFn: () => adminService.fetchDeactivationImpact(userId!),
    enabled: enabled && !!userId,
    staleTime: 0,
  })

export const assignableDoerSearchOptions = (search = '') =>
  queryOptions({
    queryKey: [...adminKeys.assignableDoers, { search }] as const,
    queryFn: () => adminService.searchAssignableDoers(search),
  })

export const assignableFounderSearchOptions = (search = '') =>
  queryOptions({
    queryKey: [...adminKeys.assignableFounders, { search }] as const,
    queryFn: () => adminService.searchAssignableFounders(search),
  })

/** Candidate owners for a doer group of this kind (DOER_GROUP_ROLE_RULES). */
export const doerGroupOwnerSearchOptions = (kind: AdminDoerGroupKind, search = '') =>
  queryOptions({
    queryKey: [...adminKeys.doerGroupOwners, { kind, search }] as const,
    queryFn: () => adminService.searchDoerGroupOwners(kind, search),
  })

/** Candidate members of a doer group of this kind (DOER_GROUP_ROLE_RULES). */
export const doerGroupMemberSearchOptions = (kind: AdminDoerGroupKind, search = '') =>
  queryOptions({
    queryKey: [...adminKeys.doerGroupMembers, { kind, search }] as const,
    queryFn: () => adminService.searchDoerGroupMembers(kind, search),
  })

/** Doers for the Activity Log picker, closest match first (ClickUp 14zf25zmr34). */
export const activityLogDoerSearchOptions = (search: string) =>
  queryOptions({
    queryKey: [...adminKeys.activityLogDoers, { search }] as const,
    queryFn: () => adminService.searchActivityLogDoers(search),
  })

/** Searches every active user, any role — candidate members of a group chat. */
export const groupChatMemberSearchOptions = (search = '') =>
  queryOptions({
    queryKey: [...adminKeys.groupChatMembers, { search }] as const,
    queryFn: () => adminService.searchGroupChatMembers(search),
  })

/** Searches all users (any role) for use as a coaching-call coach. */
export const coachSearchOptions = (search = '') =>
  queryOptions({
    queryKey: [...adminKeys.coachSearch, { search }] as const,
    queryFn: async () => {
      const res = await adminService.fetchProfiles(1, 20, search || undefined)
      return res.users
    },
  })

export const adminStatsOptions = () =>
  queryOptions({
    queryKey: adminKeys.stats,
    queryFn: adminService.fetchStats,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

export const adminFinOpsStatsOptions = () =>
  queryOptions({
    queryKey: [...adminKeys.stats, 'finops'] as const,
    queryFn: adminService.fetchFinOpsStats,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

export const adminPilotStatsOptions = () =>
  queryOptions({
    queryKey: [...adminKeys.stats, 'pilot'] as const,
    queryFn: adminService.fetchPilotStats,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

export const adminAgentVersionDistributionOptions = () =>
  queryOptions({
    queryKey: [...adminKeys.stats, 'agent-versions'] as const,
    queryFn: adminService.fetchAgentVersionDistribution,
    staleTime: 5 * 60 * 1000,
  })

// staleTime 0, unlike its siblings: the API stopped caching this report so ops
// can export it and compare it with the database (ClickUp 14zf25zkw24), and a
// five-minute client copy would undo that every time the tab is reopened.
// No refetch on window focus, though: the report is read, exported and
// compared, so it holds still, "As of" included, until the admin presses
// Refresh or reopens the tab.
export const adminAgentEnrolmentGapsOptions = () =>
  queryOptions({
    queryKey: [...adminKeys.stats, 'agent-enrolment'] as const,
    queryFn: adminService.fetchAgentEnrolmentGaps,
    staleTime: 0,
    refetchOnWindowFocus: false,
  })

export const adminFounderBusinessBriefOptions = (founderId: string) =>
  queryOptions({
    queryKey: [...adminKeys.founderBrief, founderId] as const,
    queryFn: () => adminService.fetchFounderBusinessBrief(founderId),
    enabled: !!founderId,
  })

/**
 * Admin-managed founder categories. Archived ones are left out unless asked
 * for, so every assignment picker that calls this with no argument offers only
 * categories a founder can actually be given.
 */
export const founderCategoriesOptions = (includeArchived = false) =>
  queryOptions({
    queryKey: [...adminKeys.categories, includeArchived] as const,
    queryFn: () => adminService.fetchFounderCategories(includeArchived),
    staleTime: 5 * 60 * 1000,
  })

export const doingTeamsOptions = () =>
  queryOptions({
    queryKey: adminKeys.doingTeams,
    queryFn: adminService.fetchDoingTeams,
  })

/** All doer groups with owner + member count. */
export const doerGroupsOptions = () =>
  queryOptions({
    queryKey: adminKeys.doerGroups,
    queryFn: adminService.fetchDoerGroups,
  })

/** A single doer group with its owner and member list. */
export const doerGroupOptions = (groupId: string) =>
  queryOptions({
    queryKey: [...adminKeys.doerGroup, groupId] as const,
    queryFn: () => adminService.fetchDoerGroup(groupId),
    enabled: !!groupId,
  })

/** The most recent DSL team sync run, or null before the first run. */
export const latestDslTeamSyncOptions = () =>
  queryOptions({
    queryKey: adminKeys.latestDslTeamSync,
    queryFn: adminService.getLatestDslTeamSync,
  })

/** Every independent group chat with its members and provisioning admin. */
export const groupChatsOptions = (includeArchived = false) =>
  queryOptions({
    queryKey: [...adminKeys.groupChats, includeArchived] as const,
    queryFn: () => adminService.fetchGroupChats(includeArchived),
  })

/** A single group chat with its member list. */
export const groupChatOptions = (groupChatId: string) =>
  queryOptions({
    queryKey: [...adminKeys.groupChat, groupChatId] as const,
    queryFn: () => adminService.fetchGroupChat(groupChatId),
    enabled: !!groupChatId,
  })

/** Admins assignable as the owner of a feedback item. */
export const assignableAdminsOptions = (search?: string) =>
  queryOptions({
    queryKey: [...adminKeys.assignableAdmins, search ?? ''] as const,
    queryFn: () => adminService.searchAssignableAdmins(search),
  })

/**
 * Recent coaching sessions (newest first, capped at `limit`), optional status
 * filter and optional `doerId` filter (scopes to one doer's sessions).
 */
export const adminCoachingSessionsOptions = (status?: CoachingSessionStatus, limit = 50, doerId?: string) =>
  queryOptions({
    queryKey: [...adminKeys.coachingSessions, { status, limit, doerId }] as const,
    queryFn: () => adminService.fetchCoachingSessions(status, limit, doerId),
  })

/** Full coaching-session record: session + challenges + SMART actions + escalations. */
export const adminCoachingSessionOptions = (id: string) =>
  queryOptions({
    queryKey: [...adminKeys.coachingSession, id] as const,
    queryFn: () => adminService.fetchCoachingSession(id),
    enabled: !!id,
  })

/** Mark a coaching session abandoned so the doer can start another weekly chat. */
export function useResetCoachingSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => adminService.resetCoachingSession(id),
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.coachingSessions })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.coachingSession, session.id] })
      queryClient.invalidateQueries({ queryKey: adminKeys.dslAgent })
      getNotifier().success('Coaching session reset')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to reset coaching session')
    },
  })
}

/** DSL doer-coach agent state: current KB version + live session count. */
export const adminDslAgentOptions = () =>
  queryOptions({
    queryKey: adminKeys.dslAgent,
    queryFn: () => adminService.fetchDslAgent(),
  })

/**
 * CX Risk Dashboard list — latest founder-relationship risk score per
 * (workspace, doer) across the platform, names joined. Optional band filter.
 */
export const cxRiskRelationshipsOptions = (band?: FounderRiskBand) =>
  queryOptions({
    queryKey: [...adminKeys.cxRisk, { band: band ?? null }] as const,
    queryFn: () => adminService.fetchCxRiskRelationships(band),
  })

/** Escalation-officer queue with filters (status, severity, owner) + sort. */
export const adminEscalationsOptions = (filters: EscalationQueueFilters = {}) =>
  queryOptions({
    queryKey: [...adminKeys.escalations, filters] as const,
    queryFn: () => adminService.fetchEscalations(filters),
  })

/** Single escalation record. */
export const adminEscalationOptions = (id: string) =>
  queryOptions({
    queryKey: [...adminKeys.escalation, id] as const,
    queryFn: () => adminService.fetchEscalation(id),
    enabled: !!id,
  })

/** The record's change timeline (audit_log-derived): who changed what, when. */
export const adminEscalationHistoryOptions = (id: string) =>
  queryOptions({
    queryKey: [...adminKeys.escalationHistory, id] as const,
    queryFn: () => adminService.fetchEscalationHistory(id),
    enabled: !!id,
    // Append-only, and it only moves when this user acts — at which point the
    // mutations invalidate it explicitly. The default 5s would make every window
    // focus refetch a timeline that costs 5 DB reads to assemble.
    staleTime: 60_000,
  })

/** Staff who can own a queue item (escalation_officer + admin) — assignee picker. */
export const adminEscalationAssigneesOptions = () =>
  queryOptions({
    queryKey: adminKeys.escalationAssignees,
    queryFn: () => adminService.fetchEscalationAssignees(),
    staleTime: 5 * 60 * 1000,
  })

/**
 * Escalation Officer console — the acting officer's pod roster, scoped
 * escalation queue, and headline stats. Distinct from adminEscalationsOptions
 * (which sees every escalation); this is "me"-scoped to owned doer groups.
 */
export const escalationConsoleOptions = () =>
  queryOptions({
    queryKey: adminKeys.escalationConsole,
    queryFn: () => adminService.fetchEscalationConsole(),
  })

/**
 * Both escalation mutations return the record in its full joined shape — the
 * same thing the detail query fetches — so write it straight into the cache.
 * Invalidating instead would refetch the mounted detail query for a
 * byte-identical row, and that read costs a pod-scope resolution server-side.
 *
 * The history and console queries genuinely changed, so those are invalidated;
 * the escalations list is only marked stale, and refetches when next viewed.
 */
function cacheEscalation(queryClient: QueryClient, escalation: AdminEscalation) {
  queryClient.setQueryData<AdminEscalationDetail>([...adminKeys.escalation, escalation.id], {
    escalation,
  })
  queryClient.invalidateQueries({ queryKey: adminKeys.escalations })
  queryClient.invalidateQueries({ queryKey: [...adminKeys.escalationHistory, escalation.id] })
  queryClient.invalidateQueries({ queryKey: adminKeys.escalationConsole })
}

/** Claim / reassign / unclaim a queue item. Refreshes the record + the list. */
export function useAssignEscalation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      assignedTo,
      takeOver,
    }: {
      id: string
      assignedTo: string | null
      /** Explicit intent to move the item off another user — see adminService. */
      takeOver?: boolean
    }) => adminService.assignEscalation(id, assignedTo, takeOver),
    onSuccess: ({ escalation }) => {
      cacheEscalation(queryClient, escalation)
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to update assignment')
    },
  })
}

/** Move a queue item along its lifecycle, optionally capturing resolution notes. */
export function useUpdateEscalationStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      status,
      resolutionNotes,
    }: {
      id: string
      status: EscalationStatus
      resolutionNotes?: string | null
    }) => adminService.updateEscalationStatus(id, status, resolutionNotes),
    onSuccess: ({ escalation }) => {
      cacheEscalation(queryClient, escalation)
      getNotifier().success('Escalation updated')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to update escalation')
    },
  })
}

/**
 * Weekly Review reviewer queue — amber/red recaps awaiting approval, oldest
 * first. Anything here is invisible to the founder until a reviewer approves it.
 */
export const pendingWeeklyReviewsOptions = (limit: number) =>
  queryOptions({
    queryKey: [...adminKeys.weeklyReviews, limit] as const,
    queryFn: () => adminService.fetchPendingWeeklyReviews(limit),
  })

/** A single weekly review, any status (a just-approved one is no longer pending). */
export const adminWeeklyReviewOptions = (id: string) =>
  queryOptions({
    queryKey: [...adminKeys.weeklyReview, id] as const,
    queryFn: () => adminService.fetchWeeklyReview(id),
    enabled: !!id,
    // The detail page is a text editor, and the default 5s staleness makes every
    // window focus refetch under the reviewer's cursor — a refetch whose result
    // the editor then deliberately discards for any field they've touched.
    staleTime: 30_000,
  })

/**
 * A 409 means the recap left `pending_review` under us — another reviewer
 * approved it while this one had it open. The cached review and queue are stale
 * by definition, so refetch: the detail page then re-renders read-only ("no
 * longer pending") and the queue drops the row, instead of leaving live Save /
 * Approve buttons that can only 409 again.
 */
function settleWeeklyReviewConflict(queryClient: QueryClient, id: string, error: unknown) {
  if (isConflictError(error)) {
    queryClient.invalidateQueries({ queryKey: adminKeys.weeklyReviews })
    queryClient.invalidateQueries({ queryKey: [...adminKeys.weeklyReview, id] })
  }
}

/**
 * Both mutations return the review in its full joined shape — the same thing the
 * detail query fetches — so write it straight into the cache. Invalidating
 * instead would refetch the mounted detail query for a byte-identical row. The
 * queue is only marked stale: it's inactive, so nothing refetches until it's
 * next viewed.
 */
function cacheWeeklyReview(queryClient: QueryClient, review: AdminWeeklyReview) {
  queryClient.setQueryData<AdminWeeklyReviewDetail>([...adminKeys.weeklyReview, review.id], {
    review,
  })
  queryClient.invalidateQueries({ queryKey: adminKeys.weeklyReviews })
}

/** Save a reviewer's recap edits. The whole content blob is replaced. */
export function useUpdateWeeklyReviewContent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: WeeklyReviewContent }) =>
      adminService.updateWeeklyReviewContent(id, content),
    onSuccess: ({ review }) => {
      cacheWeeklyReview(queryClient, review)
      getNotifier().success('Edits saved')
    },
    onError: (error, { id }) => {
      settleWeeklyReviewConflict(queryClient, id, error)
      getNotifier().error(error instanceof Error ? error.message : 'Failed to save edits')
    },
  })
}

/** Approve → publish the recap and notify the founder in-app. */
export function useApproveWeeklyReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => adminService.approveWeeklyReview(id),
    onSuccess: ({ review }) => {
      // Drop the row from every cached page up front: the caller navigates
      // straight back to the queue, which would otherwise repaint the approved
      // review as still pending until the refetch lands.
      queryClient.setQueriesData<AdminWeeklyReviewsResponse>(
        { queryKey: adminKeys.weeklyReviews },
        (cached) => cached && { ...cached, reviews: cached.reviews.filter((r) => r.id !== review.id) },
      )
      cacheWeeklyReview(queryClient, review)
      getNotifier().success('Weekly review approved and sent to the founder')
    },
    onError: (error, id) => {
      settleWeeklyReviewConflict(queryClient, id, error)
      getNotifier().error(error instanceof Error ? error.message : 'Failed to approve review')
    },
  })
}

export const adminUserActivityOptions = (userId: string) =>
  queryOptions<UserActivity>({
    queryKey: [...adminKeys.userActivity, userId] as const,
    queryFn: () => adminService.fetchUserActivity(userId),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
  })

/** A doer's enrolled ActivityAgent devices (admin/escalation_officer only). */
export const adminDoerDevicesOptions = (doerId: string) =>
  queryOptions<AgentDeviceListResponse>({
    queryKey: [...adminKeys.doerDevices, doerId] as const,
    queryFn: () => adminService.fetchDoerDevices(doerId),
    enabled: !!doerId,
  })

/** Agent release state (stable, rollout, versions, history). Always refetched: a promote can move it behind our back. */
export const adminAgentReleasesOptions = () =>
  queryOptions<AgentReleaseState>({
    queryKey: adminKeys.agentReleases,
    queryFn: () => agentReleaseService.fetchAdminState(),
    staleTime: 0,
  })

/** Dry-run of a rollout body. Disabled until the caller has one. */
export const adminAgentReleasePreviewOptions = (body: AgentReleaseRolloutPreviewRequest | null) =>
  queryOptions<AgentReleaseRolloutPreviewResponse>({
    queryKey: [...adminKeys.agentReleases, 'preview', body] as const,
    queryFn: () => agentReleaseService.previewRollout(body as AgentReleaseRolloutPreviewRequest),
    enabled: body !== null,
  })

/**
 * A founder's onboarding run as the admin panel sees it — ClickUp 86d3ukp2h.
 *
 * `enabled` is the caller's job: only founders have a run, and asking for a
 * doer's would 409.
 */
export const adminFounderOnboardingOptions = (founderId: string, enabled = true) =>
  queryOptions<AdminOnboardingView>({
    queryKey: [...adminKeys.founderOnboarding, founderId] as const,
    queryFn: () => adminService.fetchFounderOnboarding(founderId),
    enabled: enabled && !!founderId,
  })

/**
 * Every override refetches this founder's run rather than patching the cache
 * from the response.
 *
 * Deliberate: an override recomputes the cascade server-side, so the rows that
 * changed are rarely only the one that was asked for — completing the last gate
 * step also closes step 5 and opens four more. The endpoints do return the new
 * state, but a `requeued` unblock returns the state BEFORE the replayed handler
 * runs, so writing responses into the cache would sometimes be right and
 * sometimes be a lie. One refetch is always true.
 */
function useOnboardingOverride<TArgs, TResult>(
  founderId: string,
  mutationFn: (args: TArgs) => Promise<TResult>,
  onDone: (result: TResult) => void,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: [...adminKeys.founderOnboarding, founderId],
      })
      onDone(result)
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Could not apply the override')
    },
  })
}

/** Complete (or skip) a step on the founder's behalf. */
export function useOverrideOnboardingStep(founderId: string) {
  return useOnboardingOverride(
    founderId,
    ({ step, reason, skip }: { step: OnboardingStepNumber; reason: string; skip?: boolean }) =>
      adminService.overrideOnboardingStep(founderId, step, reason, skip),
    (result) => {
      getNotifier().success(result.unlocked ? 'Step closed — the dashboard is now unlocked' : 'Step updated')
    },
  )
}

/**
 * Record that the founder signed their agreement outside the system.
 *
 * Also invalidates the founder's profile: this is the one override that can move
 * `access_status` (onboarding → active, when their brief is already complete),
 * and that lives on the user row rather than in the onboarding view.
 */
export function useMarkAgreementSigned(founderId: string) {
  const queryClient = useQueryClient()
  return useOnboardingOverride(
    founderId,
    (reason: string) => adminService.markOnboardingAgreementSigned(founderId, reason),
    (result) => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.profiles })
      getNotifier().success(
        result.unlocked
          ? 'Agreement recorded — the dashboard is now unlocked'
          : 'Agreement recorded — step 1 is closed',
      )
    },
  )
}

/** Archive the founder's run and open the next one. */
export function useResetFounderOnboarding(founderId: string) {
  return useOnboardingOverride(
    founderId,
    (reason: string) => adminService.resetFounderOnboarding(founderId, reason),
    (result) => {
      getNotifier().success(
        result.replayedSteps.length > 0
          ? `Onboarding restarted — ${result.replayedSteps.length} step(s) kept their evidence`
          : 'Onboarding restarted',
      )
    },
  )
}

/** Re-drive, or failing that force, a step an integration owes the founder. */
export function useUnblockFounderOnboarding(founderId: string) {
  return useOnboardingOverride(
    founderId,
    ({ reason, step }: { reason: string; step?: OnboardingStepNumber }) =>
      adminService.unblockFounderOnboarding(founderId, reason, step),
    (result) => {
      getNotifier().success(
        result.action === 'requeued'
          ? `Re-sent ${result.requeuedJobs} failed event(s) — the step closes when they land`
          : 'Step closed as an admin override',
      )
    },
  )
}

// Mutations
export function useCreateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      email: string
      name: string
      role: UserRole
      founder_category?: string
      doer_id?: string
      send_invite?: boolean
      agreement_signed?: boolean
    }) => adminService.createUser(body),
    onSuccess: (_, { send_invite }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.profiles })
      queryClient.invalidateQueries({ queryKey: adminKeys.assignableDoers })
      if (send_invite) {
        getNotifier().success('User created and invitation sent!')
      }
    },
    onError: (error) => {
      console.error('Error creating user:', error)
      getNotifier().error(error instanceof Error ? error.message : 'Failed to create user')
    },
  })
}

export function useCreateTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ name, clickupListId }: { name: string; clickupListId: string }) =>
      adminService.createTeam(name, clickupListId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.doingTeams })
      getNotifier().success('Team created successfully')
    },
    onError: (error) => {
      console.error('Error creating team:', error)
      getNotifier().error('Failed to create team')
    },
  })
}

export function useCreateDoerGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateDoerGroupInput) => adminService.createDoerGroup(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.doerGroups })
      getNotifier().success('Doer group created')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to create doer group')
    },
  })
}

export function useUpdateDoerGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, input }: { groupId: string; input: UpdateDoerGroupInput }) =>
      adminService.updateDoerGroup(groupId, input),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.doerGroups })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.doerGroup, groupId] })
      getNotifier().success('Doer group updated')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to update doer group')
    },
  })
}

export function useDeleteDoerGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (groupId: string) => adminService.deleteDoerGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.doerGroups })
      getNotifier().success('Doer group deleted')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to delete doer group')
    },
  })
}

export function useAddDoerGroupMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, doerId }: { groupId: string; doerId: string }) =>
      adminService.addDoerGroupMember(groupId, doerId),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.doerGroups })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.doerGroup, groupId] })
      getNotifier().success('Doer added to group')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to add doer')
    },
  })
}

export function useRemoveDoerGroupMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, doerId }: { groupId: string; doerId: string }) =>
      adminService.removeDoerGroupMember(groupId, doerId),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.doerGroups })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.doerGroup, groupId] })
      getNotifier().success('Doer removed from group')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to remove doer')
    },
  })
}

export function useCreateGroupChat() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateGroupChatInput) => adminService.createGroupChat(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.groupChats })
      getNotifier().success('Group chat created')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to create group chat')
    },
  })
}

export function useUpdateGroupChat() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupChatId, input }: { groupChatId: string; input: UpdateGroupChatInput }) =>
      adminService.updateGroupChat(groupChatId, input),
    onSuccess: (_, { groupChatId, input }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.groupChats })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.groupChat, groupChatId] })
      // A rename and an archive land on the same endpoint; name the one that
      // actually happened.
      const archiveMessage = input.archived ? 'Group chat archived' : 'Group chat restored'
      getNotifier().success(input.archived === undefined ? 'Group chat updated' : archiveMessage)
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to update group chat')
    },
  })
}

export function useAddGroupChatMembers() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupChatId, memberIds }: { groupChatId: string; memberIds: string[] }) =>
      adminService.addGroupChatMembers(groupChatId, memberIds),
    onSuccess: (_, { groupChatId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.groupChats })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.groupChat, groupChatId] })
      // The admin may have just added or removed *themselves*, and their own
      // Messages contact list is mounted alongside this page (the rail's unread
      // badge keeps it live). Nothing else would refresh it: removal emits no
      // notification, so the stale list would keep offering a chat the server
      // now refuses.
      queryClient.invalidateQueries({ queryKey: contactsOptions().queryKey })
      getNotifier().success('Member added')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to add member')
    },
  })
}

export function useRemoveGroupChatMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ groupChatId, userId }: { groupChatId: string; userId: string }) =>
      adminService.removeGroupChatMember(groupChatId, userId),
    onSuccess: (_, { groupChatId }) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.groupChats })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.groupChat, groupChatId] })
      // Same reason as adding: see useAddGroupChatMembers.
      queryClient.invalidateQueries({ queryKey: contactsOptions().queryKey })
      getNotifier().success('Member removed')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to remove member')
    },
  })
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, newRole }: { userId: string; newRole: UserRole }) =>
      adminService.updateUserRole(userId, newRole),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.profiles })
      queryClient.invalidateQueries({ queryKey: adminKeys.assignableDoers })
      getNotifier().success('User role updated successfully')
    },
    onError: (error) => {
      console.error('Error updating role:', error)
      getNotifier().error(error instanceof Error ? error.message : 'Failed to update user role')
    },
  })
}

export function useUpdateUserFields() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      fields,
    }: {
      userId: string
      fields: {
        name?: string
        email?: string
        role?: UserRole
        founder_category?: string | null
        doer_id?: string | null
        company_name?: string | null
        logo_url?: string | null
        working_hours?: WorkingHours | null
        sensitivity_profile?: SensitivityProfile | null
        is_test?: boolean
      }
    }) => adminService.updateUserFields(userId, fields),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.user, userId] })
      queryClient.invalidateQueries({ queryKey: adminKeys.profiles })
      queryClient.invalidateQueries({ queryKey: adminKeys.assignableDoers })
      getNotifier().success('User updated successfully')
    },
    onError: (error) => {
      console.error('Error updating user:', error)
      getNotifier().error(error instanceof Error ? error.message : 'Failed to update user')
    },
  })
}

/**
 * Add a doer to a founder's workspace (multi-doer add — does NOT replace
 * existing members). `founderUserId` is the founder being viewed/edited;
 * its query cache key is invalidated so the UI re-renders with the new
 * `assignment.assigned_doers[]`.
 */
export function useAddWorkspaceMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ workspaceId, userId }: { workspaceId: string; userId: string; founderUserId: string }) =>
      adminService.addWorkspaceMember(workspaceId, userId),
    onSuccess: (_, { userId, founderUserId }) => {
      // Invalidate BOTH ends of the membership edge so the founder's and the
      // doer's detail pages stay consistent regardless of which side acted.
      queryClient.invalidateQueries({ queryKey: [...adminKeys.user, founderUserId] })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.user, userId] })
      queryClient.invalidateQueries({ queryKey: adminKeys.profiles })
      queryClient.invalidateQueries({ queryKey: adminKeys.assignableDoers })
      getNotifier().success('Doer added to workspace')
    },
    onError: (error) => {
      console.error('Error adding workspace member:', error)
      getNotifier().error(error instanceof Error ? error.message : 'Failed to add doer')
    },
  })
}

/**
 * Assign a founder to a doer from the doer-detail page. The inverse of
 * `useAddWorkspaceMember`: the server resolves/creates the founder's workspace
 * and adds the doer to it. Invalidates both the doer's and the founder's caches
 * so both detail pages reflect the new edge.
 */
export function useAssignFounderToDoer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ doerId, founderUserId }: { doerId: string; founderUserId: string }) =>
      adminService.assignFounderToDoer(doerId, founderUserId),
    onSuccess: (_, { doerId, founderUserId }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.user, doerId] })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.user, founderUserId] })
      queryClient.invalidateQueries({ queryKey: adminKeys.profiles })
      queryClient.invalidateQueries({ queryKey: adminKeys.assignableFounders })
      getNotifier().success('Founder assigned')
    },
    onError: (error) => {
      console.error('Error assigning founder to doer:', error)
      getNotifier().error(error instanceof Error ? error.message : 'Failed to assign founder')
    },
  })
}

export function useRemoveWorkspaceMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      workspaceId,
      userId,
      reassignments,
    }: {
      workspaceId: string
      userId: string
      founderUserId: string
      reassignments?: PlaybookReassignment[]
    }) => adminService.removeWorkspaceMember(workspaceId, userId, reassignments),
    onSuccess: (_, { userId, founderUserId }) => {
      // Invalidate BOTH ends of the membership edge (founder + doer).
      queryClient.invalidateQueries({ queryKey: [...adminKeys.user, founderUserId] })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.user, userId] })
      queryClient.invalidateQueries({ queryKey: adminKeys.profiles })
      queryClient.invalidateQueries({ queryKey: adminKeys.assignableDoers })
      getNotifier().success('Doer removed from workspace')
    },
    onError: (error) => {
      console.error('Error removing workspace member:', error)
      getNotifier().error(error instanceof Error ? error.message : 'Failed to remove doer')
    },
  })
}

export function useUpdateFounderBusinessBrief(founderId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (brief: BusinessBriefData) => adminService.updateFounderBusinessBrief(founderId, brief),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.founderBrief, founderId] as const })
      getNotifier().success('Business brief updated successfully')
    },
    onError: (error) => {
      console.error('Error updating founder business brief:', error)
      getNotifier().error('Failed to update business brief')
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => adminService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.profiles })
      queryClient.invalidateQueries({ queryKey: adminKeys.assignableDoers })
      getNotifier().success('User deleted successfully')
    },
    onError: (error: Error) => {
      console.error('Error deleting user:', error)
      getNotifier().error(error.message || 'Failed to delete user')
    },
  })
}

/**
 * Enable/disable a user. Disabling is a serious action — the calling dialog
 * shows the impact first and passes confirmUnassign=true so the doer cascade
 * (unassign in-flight playbooks, leave pods) runs in one request. Invalidates
 * the user's detail cache plus every list/picker so the new status propagates.
 */
export function useSetUserActive() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      active,
      confirmUnassign,
    }: {
      userId: string
      active: boolean
      confirmUnassign?: boolean
    }) => adminService.setUserActive(userId, active, confirmUnassign),
    onSuccess: (_, { userId, active }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.user, userId] })
      queryClient.invalidateQueries({ queryKey: [...adminKeys.deactivationImpact, userId] })
      queryClient.invalidateQueries({ queryKey: adminKeys.profiles })
      queryClient.invalidateQueries({ queryKey: adminKeys.assignableDoers })
      queryClient.invalidateQueries({ queryKey: adminKeys.assignableFounders })
      getNotifier().success(active ? 'User enabled' : 'User disabled')
    },
    onError: (error: Error) => {
      console.error('Error updating user status:', error)
      getNotifier().error(error.message || 'Failed to update user status')
    },
  })
}

export function useResendInvitation() {
  return useMutation({
    mutationFn: ({ email, name, role }: { email: string; name: string; role: string }) =>
      adminService.resendInvitation(email, name, role),
    onSuccess: () => {
      getNotifier().success('Invitation resent successfully')
    },
    onError: (error: Error) => {
      console.error('Error resending invitation:', error)
      getNotifier().error(error.message || 'Failed to resend invitation')
    },
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (email: string) => authService.requestPasswordResetAuth0(email),
    onSuccess: () => {
      getNotifier().success('Password reset email sent')
    },
    onError: (error: Error) => {
      console.error('Error sending password reset:', error)
      getNotifier().error(error.message || 'Failed to send password reset email')
    },
  })
}

export function useSetTempPassword() {
  return useMutation({
    mutationFn: (email: string) => adminService.setTempPassword(email),
    onSuccess: () => {
      getNotifier().success('Temporary password set')
    },
    onError: (error: Error) => {
      console.error('Error setting temporary password:', error)
      getNotifier().error(error.message || 'Failed to set temporary password')
    },
  })
}

/**
 * Revoke a doer's device — blocks it from re-enrolling (administrative
 * revocation of an install, not adversarial containment; doesn't yet cut
 * off an already-connected agent's access — see the agent-devices design
 * spec, and DevicesTabPanel's ingest-enforcement note).
 */
export function useRevokeDoerDevice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ doerId, deviceId }: { doerId: string; deviceId: string }) =>
      adminService.revokeDoerDevice(doerId, deviceId),
    onSuccess: (_, { doerId }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.doerDevices, doerId] })
      getNotifier().success('Device revoked')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to revoke device')
    },
  })
}

/**
 * Allow a revoked device to re-enroll — the mirror of
 * {@link useRevokeDoerDevice}. Only meaningful once revocation is sticky
 * (2026-08-11): before that, a revoked device could just re-enroll on its
 * own, with no admin action needed.
 */
export function useUnrevokeDoerDevice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ doerId, deviceId, reason }: { doerId: string; deviceId: string; reason: string }) =>
      adminService.unrevokeDoerDevice(doerId, deviceId, reason),
    onSuccess: (_, { doerId }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.doerDevices, doerId] })
      getNotifier().success('Device allowed to re-enroll')
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to allow re-enrollment')
    },
  })
}

/**
 * Both release mutations invalidate in `onSettled`, not `onSuccess`: a promote
 * can 500 after `latest.json` has already moved, so the page must refetch after
 * errors too. No error toast; the page renders the error inline.
 */
export function usePutAgentReleaseRollout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: PutAgentReleaseRolloutRequest) => agentReleaseService.putRollout(body),
    onSuccess: () => {
      getNotifier().success('Rollout saved')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.agentReleases })
    },
  })
}

/** Lets the page see a promote in flight anywhere (`useIsMutating`), so rows can't race. */
export const PROMOTE_AGENT_RELEASE_KEY = ['agent-release', 'promote'] as const

export function usePromoteAgentRelease() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: PROMOTE_AGENT_RELEASE_KEY,
    mutationFn: (version: string) => agentReleaseService.promote(version),
    onSuccess: (_, version) => {
      getNotifier().success(`Version ${version} promoted to stable`)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.agentReleases })
    },
  })
}

export const adminPlaybookBySlugOptions = (slug: string) =>
  queryOptions({
    queryKey: [...adminKeys.playbookBySlug, slug] as const,
    queryFn: () => adminService.fetchPlaybookBySlug(slug),
    enabled: !!slug,
  })

export const adminPlaybookDoerSchemaOptions = (slug: string) =>
  queryOptions({
    queryKey: [...adminKeys.playbookDoerSchema, slug] as const,
    queryFn: () => adminService.fetchPlaybookDoerSchema(slug),
  })

export function useSavePlaybookFounderSchema(slug: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: SchemaField[] }) =>
      adminService.updatePlaybookFounderSchema(id, fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.playbookBySlug, slug] })
      // The schema editor is a tab of the CMS editor now, and the schema decides
      // publish readiness — so its own cache, not the retired `playbookManager`
      // key, is what has to go stale.
      queryClient.invalidateQueries({ queryKey: playbookCmsKeys.all })
      getNotifier().success('Founder schema saved successfully')
    },
    onError: (error) => {
      console.error('Error saving founder schema:', error)
      getNotifier().error('Failed to save founder schema')
    },
  })
}

export function useSavePlaybookDoerSchema(slug: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, fields }: { id: string; fields: SchemaField[] }) =>
      adminService.updatePlaybookDoerSchema(id, fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.playbookDoerSchema, slug] })
      queryClient.invalidateQueries({ queryKey: playbookCmsKeys.all })
      getNotifier().success('Doer schema saved successfully')
    },
    onError: (error) => {
      console.error('Error saving doer schema:', error)
      getNotifier().error('Failed to save doer schema')
    },
  })
}

/**
 * Every founder-category surface reads through `adminKeys.categories` (the
 * list, both archived variants) or `adminKeys.categoryDetail` (one category
 * plus its recommended playbooks), so a write invalidates both prefixes rather
 * than trying to name the variants it touched.
 */
function invalidateFounderCategories(queryClient: QueryClient, code?: string) {
  queryClient.invalidateQueries({ queryKey: adminKeys.categories })
  queryClient.invalidateQueries({
    queryKey: code ? ([...adminKeys.categoryDetail, code] as const) : adminKeys.categoryDetail,
  })
}

export function useCreateFounderCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: FounderCategoryCreateInput) => adminService.createCategory(input),
    onSuccess: (category) => {
      invalidateFounderCategories(queryClient)
      getNotifier().success(`Category ${category.code} created`)
    },
    onError: (error) => {
      getNotifier().error(error instanceof Error ? error.message : 'Failed to create category')
    },
  })
}

/**
 * Edit a category, or archive/restore it with `is_active`. The API refuses an
 * archive that would strand founders (409) and answers with the count, which
 * is the only actionable part of that refusal.
 */
export function useUpdateFounderCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ code, input }: { code: string; input: FounderCategoryUpdateInput }) =>
      adminService.updateCategory(code, input),
    onSuccess: (category, { input }) => {
      invalidateFounderCategories(queryClient, category.code)
      if (input.is_active === false) getNotifier().success(`Category ${category.code} archived`)
      else if (input.is_active === true) getNotifier().success(`Category ${category.code} restored`)
      else getNotifier().success('Category updated')
    },
    onError: (error) => {
      getNotifier().error(
        assignedCategoryMessage(error) ??
          (error instanceof Error ? error.message : 'Failed to update category'),
      )
    },
  })
}

/** A refused archive, said with the number of founders still holding the code. */
function assignedCategoryMessage(error: unknown): string | null {
  if (!isConflictError(error)) return null
  const assigned = readErrorBody(error)?.assigned_users
  if (typeof assigned !== 'number') return null
  return `Still assigned to ${assigned} founder${assigned === 1 ? '' : 's'} — reassign them first`
}

export const adminCategoryDetailOptions = (code: string) =>
  queryOptions({
    queryKey: [...adminKeys.categoryDetail, code] as const,
    queryFn: () => adminService.fetchCategoryWithPlaybooks(code),
    enabled: !!code,
  })

export function useAddCategoryPlaybooks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ code, playbookIds }: { code: string; playbookIds: string[] }) =>
      adminService.addCategoryPlaybooks(code, playbookIds),
    onSuccess: (_, { code }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.categoryDetail, code] as const })
      getNotifier().success('Playbook added')
    },
    onError: () => getNotifier().error('Failed to add playbook'),
  })
}

export function useUpdateCategoryPlaybook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      code,
      playbookId,
      updates,
    }: {
      code: string
      playbookId: string
      updates: { is_optional?: boolean; display_order?: number }
    }) => adminService.updateCategoryPlaybook(code, playbookId, updates),
    onMutate: async ({ code, playbookId, updates }) => {
      // display_order reorder is handled synchronously in handleDragEnd to prevent flash
      if (updates.display_order !== undefined) return

      await queryClient.cancelQueries({ queryKey: [...adminKeys.categoryDetail, code] as const })
      const previous = queryClient.getQueryData<CategoryWithRecommendedPlaybooks>([
        ...adminKeys.categoryDetail,
        code,
      ] as const)

      if (previous && updates.is_optional !== undefined) {
        queryClient.setQueryData<CategoryWithRecommendedPlaybooks>(
          [...adminKeys.categoryDetail, code] as const,
          {
            ...previous,
            recommended_playbooks: previous.recommended_playbooks.map((rp) =>
              rp.playbook_id === playbookId ? { ...rp, is_optional: updates.is_optional! } : rp,
            ),
          },
        )
      }

      return { previous }
    },
    onError: (_err, { code }, context) => {
      if (context?.previous) {
        queryClient.setQueryData([...adminKeys.categoryDetail, code] as const, context.previous)
      }
      getNotifier().error('Failed to update playbook')
    },
    onSuccess: (_, { updates }) => {
      if (updates.is_optional !== undefined) {
        getNotifier().success(updates.is_optional ? 'Marked as optional' : 'Marked as required')
      }
    },
    onSettled: (_, __, { code }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.categoryDetail, code] as const })
    },
  })
}

export function useRemoveCategoryPlaybook() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ code, playbookId }: { code: string; playbookId: string }) =>
      adminService.removeCategoryPlaybook(code, playbookId),
    onSuccess: (_, { code }) => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.categoryDetail, code] as const })
      getNotifier().success('Playbook removed')
    },
    onError: () => getNotifier().error('Failed to remove playbook'),
  })
}
